import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LogOut, Users, Bell, Menu, X, ChevronRight, ChevronDown,
  LayoutDashboard, FileText, Folder, UploadCloud, Users2, CircleDot, UserPlus, Settings,
} from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

/**
 * ClientLayout — collapsible left sidebar for the client portal.
 *
 * Structure:
 *  - Top: Logo (links to /portal) + collapse toggle
 *  - Projects: each project expandable with Overview / Documents / Uploads / Profile
 *  - Team (client_admin only)
 *  - Notifications (with unread badge)
 *  - Bottom: Theme toggle · user info · sign out
 *  - Mobile: hamburger-triggered overlay
 */
export function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState({}); // { [projectId]: bool }
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const isClientAdmin = user && user.role === "client" && (user.permission === "edit" || user.client_role === "admin");

  // Extract active project id from the URL so the matching sidebar item auto-expands
  const activeProjectId = useMemo(() => {
    const m = location.pathname.match(/\/portal\/projects\/([^/]+)/);
    return m?.[1] || null;
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: active }, { data: arch }] = await Promise.all([
          api.get("/projects", { params: { archived: false } }),
          api.get("/projects", { params: { archived: true } }),
        ]);
        if (cancelled) return;
        setProjects([...active, ...arch]);
      } catch (_e) {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]); // refetch when navigating (e.g. project settings updated)

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setUnread(data.filter((n) => !n.read).length);
      } catch (_e) { /* notification polling is best-effort */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]); // re-poll on navigation so badge clears after mark-all-read

  // Auto-expand the project whose route is active
  useEffect(() => {
    if (activeProjectId) {
      setExpanded((e) => ({ ...e, [activeProjectId]: true }));
    }
  }, [activeProjectId]);

  // Close mobile drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const onLogout = async () => { await logout(); navigate("/login"); };

  const toggleProject = (pid) =>
    setExpanded((e) => ({ ...e, [pid]: !e[pid] }));

  return (
    <div className="min-h-screen bg-[color:var(--atech-bg)] text-[color:var(--atech-text)] flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="client-sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={[
          "fixed lg:sticky top-0 left-0 z-50 h-screen flex flex-col",
          "w-[280px] shrink-0 border-r border-[color:var(--atech-border)]",
          "bg-[color:var(--atech-surface)]/90 backdrop-blur",
          "transform transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
        data-testid="client-sidebar"
      >
        {/* Header */}
        <div className="px-5 py-5 flex items-center justify-between border-b border-[color:var(--atech-border)]">
          <NavLink to="/portal" data-testid="client-home-link" className="flex items-center">
            <Logo />
          </NavLink>
          <button
            className="lg:hidden text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-close-mobile"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 text-sm" data-testid="client-sidebar-nav">
          <SidebarLink
            to="/portal"
            end
            icon={LayoutDashboard}
            label="Dashboard"
            testId="nav-dashboard"
          />

          {/* Projects group */}
          <div className="mt-5">
            <div className="px-3 pb-2 label text-[color:var(--atech-text-muted)]">
              Projects
            </div>
            {projects.length === 0 && (
              <div className="px-3 py-2 text-xs text-[color:var(--atech-text-muted)]">
                No projects yet.
              </div>
            )}
            <ul className="space-y-0.5">
              {projects.map((p) => {
                const isOpen = !!expanded[p.id];
                const isActive = activeProjectId === p.id;
                return (
                  <li key={p.id} data-testid={`nav-project-${p.id}`}>
                    <button
                      onClick={() => {
                        toggleProject(p.id);
                        if (!isActive) navigate(`/portal/projects/${p.id}`);
                      }}
                      className={[
                        "w-full flex items-center gap-2 px-3 py-2 rounded-[2px] transition-colors text-left",
                        isActive
                          ? "bg-[color:var(--atech-surface-hover)] text-[color:var(--atech-text)] border-l-2 border-[color:var(--atech-copper)] pl-[10px]"
                          : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:bg-[color:var(--atech-surface-hover)]/60",
                      ].join(" ")}
                      data-testid={`nav-project-toggle-${p.id}`}
                    >
                      {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <CircleDot size={10} className={p.is_on_hold ? "text-[#F59E0B]" : p.is_archived ? "text-[color:var(--atech-text-muted)]" : "text-[color:var(--atech-copper)]"} />
                      <span className="flex-1 min-w-0 truncate text-[13px]">
                        {p.project_name}
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="mt-0.5 mb-1 ml-5 pl-3 border-l border-[color:var(--atech-border)] space-y-0.5">
                        <SubLink to={`/portal/projects/${p.id}`} end icon={FileText} label="Overview" testId={`nav-project-${p.id}-overview`} />
                        <SubLink to={`/portal/projects/${p.id}/documents`} icon={Folder} label="Documents" testId={`nav-project-${p.id}-documents`} />
                        {p.client_uploads_enabled && (
                          <SubLink to={`/portal/projects/${p.id}/uploads`} icon={UploadCloud} label="Uploads" testId={`nav-project-${p.id}-uploads`} />
                        )}
                        <SubLink to={`/portal/projects/${p.id}/team`} icon={Users2} label="Team" testId={`nav-project-${p.id}-team`} />
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Account group */}
          <div className="mt-6 pt-4 border-t border-[color:var(--atech-border)] space-y-0.5">
            {isClientAdmin && (
              <>
                <SidebarLink to="/portal/team" icon={Users} label="Team" testId="nav-team" />
                <SidebarLink to="/portal/team?invite=1" icon={UserPlus} label="Invite teammate" testId="nav-invite-teammate" />
              </>
            )}
            <SidebarLink
              to="/portal/notifications"
              icon={Bell}
              label="Notifications"
              badge={unread > 0 ? unread : null}
              testId="nav-notifications"
            />
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-[color:var(--atech-border)] p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <NavLink to="/portal/account" className="flex-1 min-w-0 group" data-testid="client-account-link">
              <div className="text-[13px] text-[color:var(--atech-text)] truncate group-hover:text-[color:var(--atech-copper)] transition-colors" data-testid="client-email">
                {user?.name || user?.email}
              </div>
              <div className="label text-[color:var(--atech-text-muted)]">
                {isClientAdmin ? "Client admin" : "Client"} · Account
              </div>
            </NavLink>
            <ThemeToggle variant="icon" showLabel={false} testId="client-theme-toggle" />
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] border border-[color:var(--atech-border)] hover:border-[color:var(--atech-text-dim)] rounded-[2px] py-2 transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-[color:var(--atech-border)] bg-[color:var(--atech-surface)]/85 backdrop-blur">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] p-1 -ml-1"
            data-testid="sidebar-open-mobile"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Logo compact />
          <div className="w-6" />
        </header>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, end, icon: Icon, label, badge, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2 rounded-[2px] transition-colors",
          isActive
            ? "bg-[color:var(--atech-surface-hover)] text-[color:var(--atech-text)] border-l-2 border-[color:var(--atech-copper)] pl-[10px]"
            : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:bg-[color:var(--atech-surface-hover)]/60",
        ].join(" ")
      }
      data-testid={testId}
    >
      <Icon size={14} />
      <span className="flex-1 text-[13px]">{label}</span>
      {badge != null && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-mono rounded-full bg-[color:var(--atech-copper)] text-white"
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function SubLink({ to, end, icon: Icon, label, testId }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          [
            "flex items-center gap-2 px-3 py-1.5 rounded-[2px] text-[12.5px] transition-colors",
            isActive
              ? "text-[color:var(--atech-copper)]"
              : "text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]",
          ].join(" ")
        }
        data-testid={testId}
      >
        <Icon size={12} /> {label}
      </NavLink>
    </li>
  );
}

export default ClientLayout;
